import { z } from 'zod';

import {
  codec,
  useQueryParams,
  type UseQueryParamsResult,
} from '@/hooks/router/use-query-params';

import type { ConditionViolationsStatus } from '../entities/condition-violation-status';
import type { ConditionsViolationTimeseriesData } from '../entities/condition-violation-timeseries';
import type { GaugeMetricsMap } from '../entities/gauge-metric';
import type { PenaltyDetail } from '../entities/penalty-detail';
import type { PenaltyOverview } from '../entities/penalty-overview';
import type { PenaltyTimeseriesData } from '../entities/penalty-timeseries';
import type { WarrantyIDStatus } from '../entities/warranty-id-status';
import type { PenaltyOverviewChartData } from '../entities/warranty-penalty-chart-data';

import type { RequestOptions } from './utils/request-options';

export type Site = {
  id: string;
  groupFullId: string;
  fullFrontendId: string;
};

export type Level = {
  accureName: string;
  customerName: string;
};

export namespace IWarrantyRepository {
  // --- overview table ---
  export type GetAllWarrantyIDStatusInput = {
    filters: Filters;
    assetFullFrontendId: string;
    assetId: string;
    // Customer ID for customer-specific transformations
    customerId?: string;
  };
  export type GetAllWarrantyIDStatusOutput = WarrantyIDStatus[];

  // --- gauges ---
  export type GetPerformanceWarrantyStatesInput = {
    assetFullFrontendId: string;
    // Customer ID for customer-specific transformations
    customerId?: string;
  };
  export type GetPerformanceWarrantyStatesOutput = GaugeMetricsMap;

  // --- penalty section ---
  export type GetWarrantyPenaltyChartDataInput = {
    filters: Filters;
    assetFullFrontendId: string;
    assetId: string;
    // Customer ID for customer-specific transformations
    customerId?: string;
  };
  export type GetWarrantyPenaltyChartDataOutput = PenaltyOverviewChartData[];

  export type GetPenaltyOverviewInput = {
    filters: Filters;
    assetFullFrontendId: string;
    assetId: string;
    // Customer ID for customer-specific transformations
    customerId?: string;
  };
  export type GetPenaltyOverviewOutput = PenaltyOverview[];

  // --- violations section ---
  export type GetConditionViolationTimeseriesDataInput = {
    filters: Filters;
    assetFullFrontendId: string;
    assetId: string;
    // Customer ID for customer-specific transformations
    customerId?: string;
  };
  export type GetConditionViolationTimeseriesDataOutput =
    ConditionsViolationTimeseriesData[];

  export type GetConditionViolationStatusInput = {
    filters: Filters;
    assetFullFrontendId: string;
    assetId: string;
    // Customer ID for customer-specific transformations
    customerId?: string;
  };
  export type GetConditionViolationStatusOutput = ConditionViolationsStatus[];

  // --- Penalties details section ---
  // NOTE: Currently not shown!
  export type GetPenaltyDetailsInput = {
    filters: Filters;
    assetFullFrontendId: string;
  };
  export type GetPenaltyDetailsOutput = PenaltyDetail[];

  export type PenaltyTimeseriesDataInput = {
    filters: Filters;
    assetFullFrontendId: string;
  };
  export type PenaltyTimeseriesDataOutput = PenaltyTimeseriesData[];

  // --- filter queries ---
  export type ListSitesOutput = {
    sites: Site[];
  };

  export type ListLevelsOutput = {
    levels: Level[];
  };

  // --- generic entity list ---
  export type ListEntitiesInput = {
    level: BatteryLevel;
    assetFullFrontendId: string;
    assetId: string;
    /** Current filters so the backend can cascade if needed */
    filters: Filters;
  };
  export type Entity = {
    id: string;
    fullFrontendId: string;
  };
  export type ListEntitiesOutput = {
    entities: Entity[];
  };

  // --- filters ---
  // ---------------------------------------------------------------------------
  // Shared battery-level helpers used across Warranty V2
  // ---------------------------------------------------------------------------
  export const BATTERY_LEVELS = [
    'array',
    'node',
    'container',
    'bank',
    'pack',
    'module',
    'cell',
  ] as const;

  export type BatteryLevel = (typeof BATTERY_LEVELS)[number];

  // Key helper – maps a BatteryLevel to its query-param key (e.g. "bank" → "bankId")
  export type BatteryLevelIdKey = `${BatteryLevel}Id`;

  /**
   * Flexible filter map used in query parameters.
   * - `siteId` is a slug (no slash).
   * - `<level>Id` keys store fullFrontendIds (contain a slash).
   */
  export type Filters = {
    /** Slug without slash */
    siteId?: string;
    /** Client-side aggregation hint */
    aggregationLevel?: BatteryLevel | 'site';
  } & Partial<Record<BatteryLevelIdKey, string>>;

  // Lightweight runtime validator to guard against accidental misuse during
  // development (e.g. passing a fullFrontendId into siteId or vice-versa).
  // Not used in production code-paths yet, but available for unit-tests.
  export const FiltersSchema = z
    .object({
      siteId: z
        .string()
        .optional()
        .refine((v) => !v || !v.includes('/'), {
          message:
            'siteId must be a slug without a slash, e.g. "Country_Corner"',
        }),
      aggregationLevel: z.string().optional(),
    })
    // Accept dynamic level keys via passthrough and custom refinement.
    .passthrough()
    .superRefine((obj, ctx) => {
      const dynamicLevels = BATTERY_LEVELS;

      for (const lvl of dynamicLevels) {
        const key = `${lvl}Id` as const;
        const value = (obj as Record<string, unknown>)[key] as
          | string
          | undefined;
        if (value && !value.includes('/')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [key],
            message: `${key} must be a fullFrontendId containing a '/' (e.g. Country_Corner/B01)`,
          });
        }
      }
    });

  export const useGetFiltersFromQueryParams =
    (): UseQueryParamsResult<Filters> => {
      const dynamicLevels = BATTERY_LEVELS;

      const staticMappings = {
        siteId: {
          key: 'site',
          schema: z.string().optional(),
          codec: codec.string,
        },
      } as const;

      const dynamicMappings = dynamicLevels.reduce<Record<string, any>>(
        (acc, level) => {
          const fieldKey = `${level}Id` as const;
          acc[fieldKey] = {
            key: level,
            schema: z.string().optional(),
            codec: codec.string,
          };
          return acc;
        },
        {},
      );

      const aggregationMapping = {
        aggregationLevel: {
          key: 'agg',
          schema: z.string().optional(),
          codec: codec.string,
        },
      } as const;

      return useQueryParams({
        ...staticMappings,
        ...dynamicMappings,
        ...aggregationMapping,
      } as any);
    };
}

export interface IWarrantyRepository {
  key: string;

  // --- overview table ---
  getAllWarrantyIDStatus(
    input: IWarrantyRepository.GetAllWarrantyIDStatusInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetAllWarrantyIDStatusOutput>;

  // --- gauges ---
  getPerformanceWarrantyStates(
    input: IWarrantyRepository.GetPerformanceWarrantyStatesInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetPerformanceWarrantyStatesOutput>;

  // --- penalty section ---
  getWarrantyPenaltyChartData(
    input: IWarrantyRepository.GetWarrantyPenaltyChartDataInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetWarrantyPenaltyChartDataOutput>;

  getPenaltyOverview(
    input: IWarrantyRepository.GetPenaltyOverviewInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetPenaltyOverviewOutput>;

  // --- violations section ---
  getConditionViolationTimeseriesData(
    input: IWarrantyRepository.GetConditionViolationTimeseriesDataInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetConditionViolationTimeseriesDataOutput>;

  getConditionViolationsStatus(
    input: IWarrantyRepository.GetConditionViolationStatusInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetConditionViolationStatusOutput>;

  // --- Penalties details section ---
  // NOTE: Currently not shown!
  getPenaltyDetails(
    input: IWarrantyRepository.GetPenaltyDetailsInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.GetPenaltyDetailsOutput>;

  getPenaltyTimeseriesData(
    input: IWarrantyRepository.PenaltyTimeseriesDataInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.PenaltyTimeseriesDataOutput>;

  // ------ filter queries
  listSites(
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.ListSitesOutput>;

  listLevels(
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.ListLevelsOutput>;

  // --- generic entity list ---
  listEntities(
    input: IWarrantyRepository.ListEntitiesInput,
    requestOptions?: RequestOptions,
  ): Promise<IWarrantyRepository.ListEntitiesOutput>;
}
